/**
 * Game artwork.
 *
 * Banners and icons live in public/games/<id>/ and are listed in the generated
 * manifest beside this file. Looking them up through here rather than building
 * a path by hand means a game with no art degrades to the drawn glyph instead
 * of a broken image.
 */

import manifest from './gameArt.json';

type Art = { banner?: string; icon?: string };

const ART = manifest as Record<string, Art>;

export function gameBanner(gameId: string | undefined): string | null {
  if (!gameId) return null;
  return ART[gameId]?.banner ? './' + ART[gameId].banner : null;
}

export function gameIconArt(gameId: string | undefined): string | null {
  if (!gameId) return null;
  return ART[gameId]?.icon ? './' + ART[gameId].icon : null;
}

export function hasArt(gameId: string | undefined): boolean {
  return !!(gameId && ART[gameId]);
}

/**
 * Pulls the artwork into the browser cache before anybody asks for it.
 *
 * The games screen shows twenty banners at once, and starting twenty requests
 * the moment it opens is why it appeared a piece at a time. These are files
 * the app already ships, so fetching them early costs nothing but a little
 * disk cache - by the time somebody presses Games they are already there.
 *
 * Deliberately unhurried: it waits for the browser to be idle, goes a few at
 * a time, and never touches the main thread beyond creating an Image. The one
 * thing it must not do is compete with the screen somebody is actually
 * looking at.
 */
let warmed = false;

export function warmGameArt(): void {
  if (warmed || typeof window === 'undefined') return;
  warmed = true;

  /*
   * Icons only.
   *
   * Warming the banners as well pulled twenty large pictures into memory at
   * startup - on disk they are small, decoded they are several megabytes each,
   * and most of them belong to screens nobody had opened. The icons are tiny
   * and they are what every list draws; a banner loads with the screen that
   * shows it, which is late enough that nobody notices and early enough that
   * nothing waits.
   */
  const urls: string[] = [];
  for (const art of Object.values(ART)) {
    if (art.icon) urls.push('./' + art.icon);
  }
  let at = 0;
  const BATCH = 4;

  const idle: (cb: () => void) => void =
    'requestIdleCallback' in window
      ? (cb) => (window as unknown as { requestIdleCallback: (c: () => void) => void }).requestIdleCallback(cb)
      : (cb) => window.setTimeout(cb, 300);

  const step = () => {
    if (at >= urls.length) return;
    for (let i = 0; i < BATCH && at < urls.length; i++, at++) {
      const img = new Image();
      // Not urgent by definition - nothing on screen is waiting for it.
      img.decoding = 'async';
      try { (img as unknown as { fetchPriority: string }).fetchPriority = 'low'; } catch { /* older engines */ }
      img.src = urls[at]!;
    }
    idle(step);
  };

  idle(step);
}
